import { useState } from "react";
import { WorkGroup } from "./WorksManagement";
import { api, IMAGE_PROCESSOR_URL } from "../../lib/api";

interface WorksListProps {
  workGroups: WorkGroup[];
  icons: { title: string; storage_path: string }[];
  onUpdate: () => void;
}

export function WorksList({ workGroups, icons, onUpdate }: WorksListProps) {
  const [editingGroup, setEditingGroup] = useState<WorkGroup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleEdit = (group: WorkGroup) => {
    setEditingGroup(group);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("このグループを削除してもよろしいですか？")) return;

    try {
      await api.deleteWorks(id);
      onUpdate();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingGroup) return;

    try {
      await api.updateWorks(editingGroup.id, {
        name: editingGroup.name,
        icon_path: editingGroup.icon_path,
        display_order: editingGroup.display_order,
        is_visible: editingGroup.is_visible,
      });

      setEditingGroup(null);
      onUpdate();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const iconUrl = (path: string) =>
    `${IMAGE_PROCESSOR_URL}/?path=${encodeURIComponent(path)}&width=128`;

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <div className="grid grid-cols-[1fr_2fr_2fr_1fr_1fr_auto] gap-4 px-4 py-2 bg-gray-50 rounded-t-lg text-xs font-medium text-gray-500 uppercase">
        <div>ID</div>
        <div>グループ名</div>
        <div>アイコン</div>
        <div>表示順</div>
        <div>表示/非表示</div>
        <div>操作</div>
      </div>

      <div className="space-y-2">
        {workGroups.map((group) =>
          editingGroup?.id === group.id ? (
            <form
              key={group.id}
              onSubmit={handleUpdate}
              className="grid grid-cols-[1fr_2fr_2fr_1fr_1fr_auto] gap-4 px-4 py-3 bg-white rounded-lg items-center"
            >
              <div className="text-sm text-gray-500">{group.id}</div>
              <input
                type="text"
                value={editingGroup.name}
                onChange={(e) =>
                  setEditingGroup({ ...editingGroup, name: e.target.value })
                }
                className="text-sm p-1 border rounded"
                required
              />
              <div className="flex flex-col">
                <select
                  value={editingGroup.icon_path || ""}
                  onChange={(e) =>
                    setEditingGroup({
                      ...editingGroup,
                      icon_path: e.target.value,
                    })
                  }
                  className="text-sm p-1 border rounded"
                >
                  <option value="">アイコンを選択してください</option>
                  {icons.map((icon) => (
                    <option key={icon.storage_path} value={icon.storage_path}>
                      {icon.title}
                    </option>
                  ))}
                </select>
                {editingGroup.icon_path && (
                  <img
                    src={iconUrl(editingGroup.icon_path)}
                    alt={editingGroup.icon_path}
                    className="w-8 h-8 object-contain mt-1"
                  />
                )}
              </div>
              <input
                type="number"
                value={editingGroup.display_order}
                onChange={(e) =>
                  setEditingGroup({
                    ...editingGroup,
                    display_order: parseInt(e.target.value),
                  })
                }
                className="text-sm p-1 border rounded"
                required
              />
              <input
                type="checkbox"
                checked={editingGroup.is_visible}
                onChange={(e) =>
                  setEditingGroup({
                    ...editingGroup,
                    is_visible: e.target.checked,
                  })
                }
                className="rounded border-gray-300"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => setEditingGroup(null)}
                  className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                >
                  キャンセル
                </button>
              </div>
            </form>
          ) : (
            <div
              key={group.id}
              className="grid grid-cols-[1fr_2fr_2fr_1fr_1fr_auto] gap-4 px-4 py-3 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-150 items-center"
            >
              <div className="text-sm text-gray-500">{group.id}</div>
              <div className="text-sm font-medium text-gray-900">
                {group.name}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-500 break-words">
                  {icons.find((i) => i.storage_path === group.icon_path)
                    ?.title || group.icon_path}
                </div>
                {group.icon_path && (
                  <img
                    src={iconUrl(group.icon_path)}
                    alt={group.icon_path}
                    className="w-8 h-8 object-contain"
                  />
                )}
              </div>
              <div className="text-sm text-gray-500">{group.display_order}</div>
              <div className="text-sm text-gray-500">
                {group.is_visible ? "表示" : "非表示"}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(group)}
                  className="px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
                >
                  編集
                </button>
                <button
                  onClick={() => handleDelete(group.id)}
                  className="px-2 py-1 text-xs text-red-600 bg-red-100 rounded hover:bg-red-200"
                >
                  削除
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
